import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod/mini';
import { useLingui } from '@lingui/react/macro';
import { MainSectionHeader, MainSectionContent } from '@/routes/ngos/-components/main-section';
import { VerticalTabs } from '@/routes/ngos/-components/vertical-tabs';
import { FormHandleProvider, useRegisterFormHandle } from '@/routes/ngos/-components/form-context';
import { EditToggleButton } from '@/routes/ngos/-components/edit-toggle-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@worldcoo/ui/components/card';
import { useNgoProfileQuery, putNgoProfileOptions, ngoProfileOptions } from '@/lib/queries-ngo';
import { useAppForm } from '@/lib/use-app-form';
import { FormReadOnlyProvider } from '@worldcoo/ui/components/form';
import { useEffect, useState } from 'react';
import { SettingsContactsTab } from './-components/settings-contacts-tab';
import { SettingsDonationsTab } from './-components/settings-donations-tab';
import { EditFooter } from './-components/edit-footer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getLocale } from '@/lib/locale-utils';
import { uploadNgoFiles } from '@/lib/upload-to-s3';
import { settingsFormOpts } from './-components/settings-form.opts';
import { useUploadTexts } from './-components/use-upload-texts';
import { settingsFormSchema } from '@/lib/onboarding-schemas';

const tabValues = ['organization', 'contacts', 'accreditation', 'privacy', 'donations', 'languages'] as const;
type TabValue = (typeof tabValues)[number];

// TODO: we need to find a better way to associate form fields with tabs, especially for error handling. This hardcoded mapping is not scalable and error-prone.
const fieldToTab: Record<string, TabValue> = {
	// Organization
	name: 'organization',
	web: 'organization',
	taxId: 'organization',
	address: 'organization',
	city: 'organization',
	postalCode: 'organization',
	country: 'organization',
	region: 'organization',
	province: 'organization',
	privacyUrl: 'organization',
	actionScope: 'organization',
	countries: 'organization',
	logo: 'organization',
	// Contacts
	contact1Firstname: 'contacts',
	contact1Lastname: 'contacts',
	contact1Position: 'contacts',
	contact1Email: 'contacts',
	contact1Phone: 'contacts',
	contact2Firstname: 'contacts',
	contact2Lastname: 'contacts',
	contact2Position: 'contacts',
	contact2Email: 'contacts',
	contact2Phone: 'contacts',
	contactLegalFirstname: 'contacts',
	contactLegalLastname: 'contacts',
	contactLegalPosition: 'contacts',
	contactLegalId: 'contacts',
	language: 'contacts',
	allowedEmails: 'contacts',
	// Accreditation
	certifiedByFoundation: 'accreditation',
	certifiedByFoundationDescription: 'accreditation',
	corporateVolunteering: 'accreditation',
	corporateVolunteeringDescription: 'accreditation',
	// Donations
	donationCertificates: 'donations',
	bankInfo: 'donations',
	bankCurrency: 'donations',
	bankAccountName: 'donations',
	bankAccountCertificate: 'donations',
	bankAddress: 'donations',
	bankCity: 'donations',
	bankPostalCode: 'donations',
	bankCountry: 'donations',
	bankRegion: 'donations',
	bankProvince: 'donations',
	// Privacy
	privacyEmail: 'privacy',
	privacyPhone: 'privacy',
	privacyAddress: 'privacy',
	privacyCity: 'privacy',
	privacyPostalCode: 'privacy',
	privacyCountry: 'privacy',
	privacyRegion: 'privacy',
	privacyProvince: 'privacy',
	// Languages
	languages: 'languages',
	defaultLanguage: 'languages',
};

function ErrorDot({ active }: { active: boolean }) {
	return <span className={`size-2 rounded-full ${active ? 'bg-destructive-foreground' : 'bg-destructive'}`} />;
}

export const Route = createFileRoute('/ngos/settings')({
	validateSearch: z.object({
		tab: z.catch(z.optional(z.enum(tabValues)), undefined),
		editing: z.catch(z.optional(z.boolean()), undefined),
	}),
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(ngoProfileOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useLingui();
	const { tab, editing } = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/settings' });
	const activeTab: TabValue = tab ?? 'organization';
	const isEditing = editing === true;
	const [tabsWithErrors, setTabsWithErrors] = useState(new Set());

	useEffect(() => {
		if (!isEditing) setTabsWithErrors(new Set());
	}, [isEditing]);

	const errorHint = (tab: TabValue) =>
		tabsWithErrors.has(tab) ? (active: boolean) => <ErrorDot active={active} /> : undefined;

	const tabConfig = [
		{ value: 'organization' as const, label: t`Organization Details`, hint: errorHint('organization') },
		{ value: 'contacts' as const, label: t`Contact Person`, hint: errorHint('contacts') },
		{ value: 'accreditation' as const, label: t`Accreditation`, hint: errorHint('accreditation') },
		{ value: 'privacy' as const, label: t`Data Privacy`, hint: errorHint('privacy') },
		{ value: 'donations' as const, label: t`Donations`, hint: errorHint('donations') },
		{ value: 'languages' as const, label: t`Languages`, hint: errorHint('languages') },
	];

	return (
		<FormHandleProvider>
			{/* Header */}
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Settings`}</h1>}
				actions={<EditToggleButton isEditing={isEditing} navigate={navigate} />}
			/>

			{/* Content area with sidebar tabs */}
			<MainSectionContent className="!flex-row gap-12 !overflow-hidden">
				<VerticalTabs
					tabs={tabConfig}
					activeTab={activeTab}
					onTabChange={(value) =>
						navigate({
							search: (prev) => ({
								...prev,
								tab: value === 'organization' ? undefined : value,
							}),
						})
					}
				/>

				{/* Tab content */}
				<div className="min-h-0 flex-1 overflow-y-auto pt-4">
					<SettingsContent onErrorTabsChange={setTabsWithErrors} />
				</div>
			</MainSectionContent>

			{/* Portal target for footer rendered from SettingsContent (needs form context for isSubmitting) */}
			<div id="settings-footer-portal" />
		</FormHandleProvider>
	);
}

function SettingsContent({ onErrorTabsChange }: { onErrorTabsChange: (tabs: Set<TabValue>) => void }) {
	const { tab, editing } = Route.useSearch();
	const activeTab = tab ?? 'organization';
	const isEditing = editing === true;
	const ngo = useNgoProfileQuery();
	const { t, i18n } = useLingui();
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const navigate = useNavigate({ from: '/ngos/settings' });
	const queryClient = useQueryClient();
	const mutation = useMutation(putNgoProfileOptions());

	const actionScopeOptions = [
		{ value: 'waterAndEnergy', label: t`Water & Energy` },
		{ value: 'education', label: t`Education` },
		{ value: 'humanRights', label: t`Human Rights` },
		{ value: 'health', label: t`Health` },
		{ value: 'environment', label: t`Environment` },
		{ value: 'foodSecurity', label: t`Food Security` },
		{ value: 'habitatAndPoverty', label: t`Habitat & Poverty` },
	];

	const [showContact2, setShowContact2] = useState(
		!!(ngo.contact2Firstname || ngo.contact2Lastname || ngo.contact2Email),
	);

	const form = useAppForm({
		...settingsFormOpts,
		defaultValues: {
			// Organization
			name: ngo.name ?? '',
			web: ngo.web ?? '',
			taxId: ngo.taxId ?? '',
			address: ngo.address ?? '',
			city: ngo.city ?? '',
			postalCode: ngo.postalCode ?? '',
			country: ngo.country ?? '',
			region: ngo.region ?? '',
			province: ngo.province ?? '',
			privacyUrl: ngo.privacyUrl ?? '',
			actionScope: ngo.actionScope ?? [],
			countries: ngo.countries ?? [],
			logo: undefined as File | '' | undefined,

			// Contacts
			allowedEmails: (ngo.allowedEmails ?? []).map((e) => ({ _key: crypto.randomUUID() as string, email: e })),
			contact1Firstname: ngo.contact1Firstname ?? '',
			contact1Lastname: ngo.contact1Lastname ?? '',
			contact1Position: ngo.contact1Position ?? '',
			contact1Email: ngo.contact1Email ?? '',
			contact1Phone: ngo.contact1Phone ?? '',
			contact2Firstname: ngo.contact2Firstname ?? '',
			contact2Lastname: ngo.contact2Lastname ?? '',
			contact2Position: ngo.contact2Position ?? '',
			contact2Email: ngo.contact2Email ?? '',
			contact2Phone: ngo.contact2Phone ?? '',
			contactLegalFirstname: ngo.contactLegalFirstname ?? '',
			contactLegalLastname: ngo.contactLegalLastname ?? '',
			contactLegalPosition: ngo.contactLegalPosition ?? '',
			contactLegalId: ngo.contactLegalId ?? '',
			language: ngo.language ?? 'SPA',

			// Accreditation
			certifiedByFoundation: ngo.certifiedByFoundation ?? false,
			certifiedByFoundationDescription: ngo.certifiedByFoundationDescription ?? '',
			corporateVolunteering: ngo.corporateVolunteering ?? false,
			corporateVolunteeringDescription: ngo.corporateVolunteeringDescription ?? '',

			// Donations
			donationCertificates: ngo.donationCertificates ?? false,
			bankInfo: {
				wantSwiftAndCC: ngo.bankInfo?.wantSwiftAndCC ?? false,
				bankSwiftCode: ngo.bankInfo?.bankSwiftCode ?? '',
				bankAccountNumber: ngo.bankInfo?.bankAccountNumber ?? '',
				bankIBANCode: ngo.bankInfo?.bankIBANCode ?? '',
			},
			bankCurrency: ngo.bankCurrency ?? 'EUR',
			bankAccountName: ngo.bankAccountName ?? '',
			bankAccountCertificate: undefined as File | '' | undefined,
			bankAddress: ngo.bankAddress ?? '',
			bankCity: ngo.bankCity ?? '',
			bankPostalCode: ngo.bankPostalCode ?? '',
			bankCountry: ngo.bankCountry ?? ngo.country ?? '',
			bankRegion: ngo.bankRegion ?? '',
			bankProvince: ngo.bankProvince ?? '',

			// Privacy
			privacyEmail: ngo.privacyEmail ?? '',
			privacyPhone: ngo.privacyPhone ?? '',
			privacyAddress: ngo.privacyAddress ?? '',
			privacyCity: ngo.privacyCity ?? '',
			privacyPostalCode: ngo.privacyPostalCode ?? '',
			privacyCountry: ngo.privacyCountry ?? '',
			privacyRegion: ngo.privacyRegion ?? '',
			privacyProvince: ngo.privacyProvince ?? '',

			// Languages
			languages: Array.from<string>(ngo.languages ?? ['SPA']),
			defaultLanguage: ngo.defaultLanguage ?? 'SPA',
		},
		onSubmit: async ({ value }) => {
			// Upload new files directly to S3 via presigned URLs
			const uploaded = await uploadNgoFiles({ logo: value.logo, bankAccountCertificate: value.bankAccountCertificate });

			const data: Record<string, unknown> = { ...value };
			// Extract email strings, filtering out empty entries
			data.allowedEmails = value.allowedEmails.map((e) => e.email).filter((e) => e.trim() !== '');
			// Replace File objects with S3 keys, or remove unchanged fields
			if (uploaded.logo) {
				data.logo = uploaded.logo.key;
				data.logoWidth = uploaded.logo.width;
				data.logoHeight = uploaded.logo.height;
			} else if (value.logo === '') {
				data.logo = '';
			} else {
				delete data.logo;
			}
			if (uploaded.bankAccountCertificate) {
				data.bankAccountCertificate = uploaded.bankAccountCertificate;
			} else if (value.bankAccountCertificate === '') {
				data.bankAccountCertificate = '';
			} else {
				delete data.bankAccountCertificate;
			}

			const result = await mutation.mutateAsync({ data });
			if (result && typeof result === 'object' && 'error' in result) {
				const msg =
					result.error === 'FILE_TOO_LARGE'
						? t`The file is too large. Maximum allowed size is 2 GB.`
						: t`Failed to save settings. Please try again.`;
				toast.error(msg);
				return;
			}
			await queryClient.invalidateQueries({ queryKey: ngoProfileOptions().queryKey });
			const hasContact2 = !!(value.contact2Firstname || value.contact2Lastname || value.contact2Email);
			setShowContact2(hasContact2);

			// We need to use a timeout because of this issue: https://github.com/TanStack/form/issues/1681
			setTimeout(() => {
				form.reset();

				navigate({ search: (prev) => ({ ...prev, editing: undefined }) });
			});
		},
	});

	useRegisterFormHandle(form);
	const uploadTexts = useUploadTexts();

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Pre-validate to detect errors on other tabs and show error indicators
		const result = settingsFormSchema.safeParse(form.state.values);
		if (!result.success) {
			const errorTabsSet = new Set<TabValue>();
			for (const issue of result.error.issues) {
				const fieldName = String(issue.path[0]);
				const tab = fieldToTab[fieldName];
				if (tab) errorTabsSet.add(tab);
			}
			onErrorTabsChange(errorTabsSet);

			// If current tab has no errors, navigate to first tab that does
			if (!errorTabsSet.has(activeTab)) {
				const firstErrorTab = tabValues.find((t) => errorTabsSet.has(t));
				if (firstErrorTab) {
					navigate({
						search: (prev) => ({ ...prev, tab: firstErrorTab === 'organization' ? undefined : firstErrorTab }),
					});
					toast.error(t`Please fix the validation errors.`);
					return;
				}
			}
		} else {
			onErrorTabsChange(new Set());
		}

		form.handleSubmit();
	};

	return (
		<FormReadOnlyProvider value={!isEditing}>
			<form
				id="settings-edit-form"
				onSubmit={handleSubmit}
				className="flex w-full max-w-[600px] flex-col gap-6 px-8 py-6"
			>
				{activeTab === 'organization' && (
					<>
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Basic Information`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<form.AppField name="name" children={(field) => <field.TextField label={t`Organization Name`} />} />
								<form.AppField
									name="web"
									children={(field) => <field.LinkTextField label={t`Website`} placeholder="https://www.example.org" />}
								/>
								<form.AppField name="taxId" children={(field) => <field.TextField label={t`Tax ID (CIF/NIF)`} />} />
								<form.AppField name="address" children={(field) => <field.TextField label={t`Address`} />} />
								<div className="flex gap-4">
									<form.AppField
										name="city"
										children={(field) => <field.TextField label={t`City`} className="w-full" />}
									/>
									<form.AppField
										name="postalCode"
										children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
									/>
								</div>
								<form.AppField
									name="country"
									listeners={{
										onChange: ({ value }) => {
											form.setFieldValue('region', '', { dontValidate: true, dontUpdateMeta: true });
											form.setFieldValue('province', '', { dontValidate: true, dontUpdateMeta: true });
											if (value) {
												const current = form.getFieldValue('countries');
												if (!current.includes(value)) {
													form.setFieldValue('countries', [...current, value]);
												}
											}
										},
									}}
									children={(field) => (
										<field.CountryField label={t`Country`} locale={getLocale(i18n)} texts={countryFieldTexts} />
									)}
								/>
								<form.Subscribe selector={(state) => ({ country: state.values.country, region: state.values.region })}>
									{({ country, region }) => (
										<>
											<form.AppField
												name="region"
												validators={{ onChangeListenTo: ['country'] }}
												listeners={{
													onChange: () =>
														form.setFieldValue('province', '', {
															dontValidate: true,
															dontUpdateMeta: true,
														}),
												}}
												children={(field) => (
													<field.RegionField label={t`Region`} country={country} texts={regionFieldTexts} />
												)}
											/>
											<form.AppField
												name="province"
												validators={{ onChangeListenTo: ['country', 'region'] }}
												children={(field) => (
													<field.ProvinceField
														label={t`Province`}
														region={region}
														country={country}
														texts={provinceFieldTexts}
													/>
												)}
											/>
										</>
									)}
								</form.Subscribe>
							</CardContent>
						</Card>

						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Multimedia`}</CardTitle>
							</CardHeader>
							<CardContent>
								<form.AppField
									name="logo"
									children={(field) => (
										<field.ImageUploadField
											{...uploadTexts.imageUpload}
											currentUrl={ngo.logo}
											label={t`Logo`}
											buttonLabel={t`Upload Logo`}
											contain
											enableOpenInTab
										/>
									)}
								/>
							</CardContent>
						</Card>

						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Other Information`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<form.AppField
									name="privacyUrl"
									children={(field) => <field.LinkTextField label={t`Privacy Policy URL`} placeholder="https://..." />}
								/>
								<form.AppField
									name="actionScope"
									children={(field) => (
										<field.MultiComboboxTextField
											label={t`Action Scope`}
											search={t`Search…`}
											empty={t`No results found.`}
											options={actionScopeOptions}
										/>
									)}
								/>
								<form.AppField
									name="countries"
									children={(field) => (
										<field.MultiCountryField
											label={t`Countries of Operation`}
											locale={getLocale(i18n)}
											texts={countryFieldTexts}
										/>
									)}
								/>
							</CardContent>
						</Card>
					</>
				)}

				{activeTab === 'contacts' && (
					<SettingsContactsTab
						form={form}
						isEditing={isEditing}
						showContact2={showContact2}
						setShowContact2={setShowContact2}
					/>
				)}

				{activeTab === 'accreditation' && (
					<Card className="shadow-warm">
						<CardHeader>
							<CardTitle className="text-xl">{t`Accreditation Details`}</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-6">
							<form.AppField
								name="certifiedByFoundation"
								children={(field) => <field.SwitchField label={t`Certified by Foundation`} position="right" />}
							/>
							<form.Subscribe selector={(state) => state.values.certifiedByFoundation}>
								{(certifiedByFoundation) => (
									<div
										className="grid transition-[grid-template-rows,opacity] duration-200"
										style={{
											gridTemplateRows: certifiedByFoundation ? '1fr' : '0fr',
											opacity: certifiedByFoundation ? 1 : 0,
										}}
									>
										<div className="-m-1 overflow-hidden p-1">
											<form.AppField
												name="certifiedByFoundationDescription"
												children={(field) => <field.TextareaField label={t`Foundation Certification Details`} />}
											/>
										</div>
									</div>
								)}
							</form.Subscribe>
							<form.AppField
								name="corporateVolunteering"
								children={(field) => <field.SwitchField label={t`Corporate Volunteering`} position="right" />}
							/>
							<form.Subscribe selector={(state) => state.values.corporateVolunteering}>
								{(corporateVolunteering) => (
									<div
										className="grid transition-[grid-template-rows,opacity] duration-200"
										style={{
											gridTemplateRows: corporateVolunteering ? '1fr' : '0fr',
											opacity: corporateVolunteering ? 1 : 0,
										}}
									>
										<div className="-m-1 overflow-hidden p-1">
											<form.AppField
												name="corporateVolunteeringDescription"
												children={(field) => <field.TextareaField label={t`Corporate Volunteering Details`} />}
											/>
										</div>
									</div>
								)}
							</form.Subscribe>
						</CardContent>
					</Card>
				)}

				{activeTab === 'privacy' && (
					<Card className="shadow-warm">
						<CardHeader>
							<CardTitle className="text-xl">{t`Data Privacy Contact`}</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-6">
							<form.AppField
								name="privacyEmail"
								children={(field) => (
									<field.LinkTextField
										label={t`Privacy Contact Email`}
										placeholder="privacy@example.org"
										hrefTransform={(v) => `mailto:${v}`}
									/>
								)}
							/>
							<form.AppField
								name="privacyPhone"
								children={(field) => <field.TextField label={t`Phone`} placeholder="+34..." />}
							/>
							<form.AppField name="privacyAddress" children={(field) => <field.TextField label={t`Address`} />} />
							<div className="flex gap-4">
								<form.AppField
									name="privacyCity"
									children={(field) => <field.TextField label={t`City`} className="w-full" />}
								/>
								<form.AppField
									name="privacyPostalCode"
									children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
								/>
							</div>
							<form.AppField
								name="privacyCountry"
								listeners={{
									onChange: () => {
										form.setFieldValue('privacyRegion', '', { dontValidate: true, dontUpdateMeta: true });
										form.setFieldValue('privacyProvince', '', { dontValidate: true, dontUpdateMeta: true });
									},
								}}
								children={(field) => (
									<field.CountryField label={t`Country`} locale={getLocale(i18n)} texts={countryFieldTexts} />
								)}
							/>
							<form.Subscribe
								selector={(state) => ({
									privacyCountry: state.values.privacyCountry,
									privacyRegion: state.values.privacyRegion,
								})}
							>
								{({ privacyCountry, privacyRegion }) => (
									<>
										<form.AppField
											name="privacyRegion"
											validators={{ onChangeListenTo: ['privacyCountry'] }}
											listeners={{
												onChange: () =>
													form.setFieldValue('privacyProvince', '', {
														dontValidate: true,
														dontUpdateMeta: true,
													}),
											}}
											children={(field) => (
												<field.RegionField label={t`Region`} country={privacyCountry} texts={regionFieldTexts} />
											)}
										/>
										<form.AppField
											name="privacyProvince"
											validators={{ onChangeListenTo: ['privacyCountry', 'privacyRegion'] }}
											children={(field) => (
												<field.ProvinceField
													label={t`Province`}
													region={privacyRegion}
													country={privacyCountry}
													texts={provinceFieldTexts}
												/>
											)}
										/>
									</>
								)}
							</form.Subscribe>
						</CardContent>
					</Card>
				)}

				{activeTab === 'donations' && <SettingsDonationsTab form={form} ngo={ngo} />}

				{activeTab === 'languages' && (
					<>
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Languages`}</CardTitle>
								<CardDescription className="leading-relaxed">
									{t`Select the languages your organization supports. Content will be translated to the selected languages.`}
								</CardDescription>
							</CardHeader>
							<CardContent className="flex flex-col gap-2">
								<form.Subscribe selector={(state) => state.values.defaultLanguage}>
									{(defaultLanguage) => (
										<form.AppField name="languages">
											{(field) => (
												<field.LanguagesPickerField
													defaultText={t`Default`}
													setAsDefaultText={t`Set as default`}
													defaultLanguage={defaultLanguage}
													onDefaultLanguageChange={(lang) => form.setFieldValue('defaultLanguage', lang)}
												/>
											)}
										</form.AppField>
									)}
								</form.Subscribe>
							</CardContent>
						</Card>
					</>
				)}
			</form>
			{isEditing && (
				<EditFooter
					form={form}
					formId="settings-edit-form"
					portalId="settings-footer-portal"
					onDiscard={() => navigate({ search: (prev) => ({ ...prev, editing: undefined }) })}
				/>
			)}
		</FormReadOnlyProvider>
	);
}
